import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CreditCard, LogOut, Home, Check, ArrowLeft, Crown } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { signOut } from "@/lib/firebase";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

// Tier configuration
interface TierConfig {
  name: string;
  price: string;
  popular?: boolean;
  features: string[];
  bestFor: string;
  description: string;
  badge?: string;
}

const TIERS: Record<string, TierConfig> = {
  basic: {
    name: "Starter",
    price: "$149",
    bestFor: "Small cafés, food trucks, and new restaurants",
    description: "Simple setup for restaurants getting started",
    badge: "30-day risk free trial",
    popular: false,
    features: [
      "AI Waiter",
      "Multi-language support",
      "Digital menu (QR-based)",
      "Restaurant dashboard",
      "Menu & price management",
      "Basic AI Business Analyst",
      "Basic analytics",
    ],
  },
  growth: {
    name: "Growth",
    price: "$299",
    bestFor: "Busy casual restaurants",
    description: "Scale your operations with advanced AI features",
    popular: true,
    features: [
      "Everything in Starter, plus:",
      "AI Receptionist",
      "AI-device-enabled digital menu",
      "Reservations management",
      "Online phone ordering",
      "Advanced analytics",
    ],
  },
  pro: {
    name: "Pro",
    price: "$499",
    bestFor: "High-volume or multi-shift restaurants",
    description: "Full suite for enterprise-level volume",
    badge: "Free POS Device",
    popular: false,
    features: [
      "Everything in Growth, plus:",
      "POS integration",
      "Advanced AI Business Analyst",
      "Kitchen Display System (KDS) enabled",
      "Mobile ordering enabled",
      "Priority support",
    ],
  },
};

const TIER_ORDER = ['basic', 'growth', 'pro'];

export default function Subscribe() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);

  // Get tier from URL and check current subscription
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tierParam = params.get('tier');
    const canceled = params.get('canceled');

    if (tierParam && ['basic', 'growth', 'pro', 'starter'].includes(tierParam.toLowerCase())) {
      const mappedTier = tierParam.toLowerCase() === 'starter' ? 'basic' : tierParam.toLowerCase();
      setSelectedTier(mappedTier);
    }

    if (canceled === 'true') {
      toast({
        variant: "destructive",
        title: "Payment canceled",
        description: "You can try again when you're ready.",
      });
      window.history.replaceState({}, '', '/subscribe');
    }

    // Check if the user already has a subscription
    const checkSubscription = async () => {
      try {
        const res = await authenticatedFetch('/api/subscription/status');
        if (res.ok) {
          const data = await res.json();
          // If status is active or trialing, we mark the tier as current
          if ((data.subscriptionStatus === 'active' || data.subscriptionStatus === 'trialing') && data.subscriptionTier) {
            setCurrentTier(data.subscriptionTier.toLowerCase());
            setIsExistingUser(true);
          } else {
            // New user or inactive subscription
            setCurrentTier(null);
            setIsExistingUser(false);
          }
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    };
    checkSubscription();
  }, [toast]);

  // Handle logout
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      await apiRequest("POST", "/api/logout");
      window.location.href = '/';
    } catch (error) {
      console.error("Logout error:", error);
      window.location.href = '/';
    }
  };

  // Handle back to home (just logs out and redirects)
  const handleBackToHome = () => {
    handleLogout();
  };

  // Determine button label based on current plan vs target plan
  const getButtonLabel = (tierId: string) => {
    if (!currentTier) return 'Get Started';
    if (tierId === currentTier) return 'Current Plan';
    const currentIndex = TIER_ORDER.indexOf(currentTier);
    const targetIndex = TIER_ORDER.indexOf(tierId);
    return targetIndex > currentIndex ? 'Upgrade' : 'Downgrade';
  };

  const isCurrentPlan = (tierId: string) => tierId === currentTier;

  // Handle tier selection and checkout
  const handleSelectTier = async (tier: string) => {
    if (isCurrentPlan(tier)) return; // Don't allow selecting current plan

    setSelectedTier(tier);
    setIsCreatingCheckout(true);

    try {
      const response = await apiRequest('POST', '/api/subscription/create', { tier });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create checkout session' }));
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();

      if (data.alreadyActive) {
        navigate('/dashboard');
      } else if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create checkout session",
      });
      setIsCreatingCheckout(false);
    }
  };

  // Back to Home footer component (onboarding)
  const BackToHomeFooter = () => (
    <div className="flex flex-col items-center gap-2 mt-6 pt-4 border-t">
      <p className="text-sm text-muted-foreground">Changed your mind?</p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleBackToHome}
          disabled={isLoggingOut}
          data-testid="button-back-to-home"
        >
          {isLoggingOut ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Home className="h-4 w-4 mr-2" />
          )}
          Back to Home
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          disabled={isLoggingOut}
          data-testid="button-logout-subscribe"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Log Out
        </Button>
      </div>
    </div>
  );

  // Back to Dashboard footer (existing user)
  const BackToDashboardFooter = () => (
    <div className="flex justify-center mt-6 pt-4 border-t">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate('/admin')}
        data-testid="button-back-to-admin"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Admin Settings
      </Button>
    </div>
  );

  // If creating checkout, show loading
  if (isCreatingCheckout) {
    return (
      <div className="container max-w-2xl mx-auto py-12 flex items-center justify-center min-h-screen">
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-6 w-6" />
              {isExistingUser ? 'Upgrading Your Plan' : 'Subscribe to Waiterix'}
            </CardTitle>
            <CardDescription>
              Setting up your {selectedTier ? TIERS[selectedTier as keyof typeof TIERS]?.name : ''} subscription...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" data-testid="loader-subscription" />
            <p className="text-sm text-muted-foreground">Redirecting to checkout...</p>
            {isExistingUser ? <BackToDashboardFooter /> : <BackToHomeFooter />}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show tier selection
  return (
    <div className="container max-w-7xl mx-auto py-12 px-4 min-h-screen">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4 tracking-tight">
          {isExistingUser ? 'Manage Your Plan' : 'Choose Your Plan'}
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {isExistingUser
            ? `You're currently on the ${TIERS[currentTier || 'basic']?.name} plan. Select a new plan to upgrade.`
            : 'Premium AI-driven hospitality solutions tailored to your business needs.'}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {Object.entries(TIERS).map(([tierId, tier]) => (
          <Card
            key={tierId}
            className={`relative flex flex-col cursor-pointer transition-all hover:shadow-xl group ${selectedTier === tierId ? 'ring-2 ring-primary shadow-lg' : 'hover:-translate-y-1 border-border/50'
              } ${tier.popular ? 'border-primary/50 bg-primary/[0.02]' : ''} ${isCurrentPlan(tierId) ? 'ring-2 ring-green-500 bg-green-50/50 dark:bg-green-950/20' : ''}`}
            onClick={() => !isCreatingCheckout && !isCurrentPlan(tierId) && handleSelectTier(tierId)}
          >
            {(tier.popular || tier.badge) && !isCurrentPlan(tierId) && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                {tier.badge || "Most Popular"}
              </div>
            )}
            {isCurrentPlan(tierId) && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 whitespace-nowrap">
                <Crown className="h-3.5 w-3.5" />
                Current Plan
              </div>
            )}
            <CardHeader className="pb-4">
              <div className="mb-2">
                <CardTitle className="text-2xl">{tier.name}</CardTitle>
                <p className="text-xs text-muted-foreground font-medium mt-1 leading-relaxed">
                  Best for: {tier.bestFor}
                </p>
              </div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="text-4xl font-bold">{tier.price}</span>
                <span className="text-sm font-medium text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2 border-t pt-2 italic">
                {tier.description}
              </p>
            </CardHeader>
            <CardContent className="flex-grow flex flex-col">
              <ul className="space-y-3.5 mb-8">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-tight text-muted-foreground group-hover:text-foreground transition-colors">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto">
                <Button
                  className={`w-full py-6 text-sm font-bold uppercase tracking-wider transition-all duration-300 ${tier.popular ? "shadow-glow hover:shadow-glow-lg" : ""}`}
                  variant={isCurrentPlan(tierId) ? "secondary" : tier.popular ? "default" : "outline"}
                  disabled={isCreatingCheckout || isCurrentPlan(tierId)}
                >
                  {isCreatingCheckout && selectedTier === tierId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {getButtonLabel(tierId)}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center text-sm text-muted-foreground mb-8 max-w-md mx-auto p-4 rounded-xl border bg-muted/30">
        <p className="font-medium text-foreground mb-1">+ AI Usage Costs</p>
        <p>Billed monthly at $0.29 per minute ($0.00483/sec)</p>
        <p className="mt-2 text-[10px] uppercase tracking-wider opacity-60">All prices in USD • Billed Monthly</p>
      </div>

      {isExistingUser ? <BackToDashboardFooter /> : <BackToHomeFooter />}
    </div>
  );
}
